import React, { FunctionComponent } from "react";
import TopNav from "./topNav";
import Header from "./header";
import Sidebar from "./sidebar";
import Footer from "./footer";
import { Box, Divider, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { IMAGES } from "../../utils/constants";

interface DashboardProps {
  isLoading?: boolean;
  children: React.ReactNode;
  titleIcon?: React.ReactNode;
  title: string;
  initialBreadcrumbs: string;
  levelBreadcrumbs?: string;
  breadcrumbs: string;
  activeMenu: string;
  container?: boolean;
  containerWidth?: string;
  isOverflow?: boolean;
  showSupport?: boolean;
  containerTitle?: string | React.ReactNode;
  scrollableWidth?: string;
}

const DashboardLayout: FunctionComponent<DashboardProps> = ({
  isLoading,
  children,
  titleIcon,
  title,
  initialBreadcrumbs,
  levelBreadcrumbs,
  breadcrumbs,
  activeMenu,
  container,
  containerWidth = "100%",
  isOverflow,
  showSupport = false,
  containerTitle = "",
  scrollableWidth = '100%',
}) => {
  return (
    <Box sx={{ flexGrow: 1 }}>
      <TopNav />
      <Header />
      <Box sx={{ display: "flex", minHeight: "90vh" }}>
        <Sidebar activeMenu={activeMenu} />

        {/* Page code start here */}
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Box>
            <Box
              sx={{
                p: 3,
                position: "relative",
                backgroundColor: "#fff",
                overflow: "hidden",
                "&::before, &::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  width: "100%",
                  height: "20px",
                  zIndex: 1,
                },
                "&::before": {
                  top: 0,
                  background:
                    "linear-gradient(to bottom, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0))",
                },
                "&::after": {
                  bottom: 0,
                  background:
                    "linear-gradient(to top, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0))",
                },
              }}
            >
              <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start",
                mb: 1,
                pl: 4,
                pr: 2
              }}>
                <Typography
                  variant="h5"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {titleIcon} {title}
                </Typography>
                {/* {title === "Welcome To Barksdale Extranet" && ( */}
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    gap: 0.5,
                    alignItems: "flex-end",
                    minWidth: "320px"
                  }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                      <span style={{ fontWeight: "bold", color: "#c3183c" }}>Customer Care:</span>{" "}
                      800.835.1060 or{" "}
                      <a 
                        href="mailto:orders@barksdale.com" 
                        style={{ 
                          color: "#1976d2", 
                          textDecoration: "underline",
                          textDecorationColor: "#1976d2"
                        }}
                      >
                        orders@barksdale.com
                      </a>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", lineHeight: 1.2 }}>
                      <span style={{ fontWeight: "bold", color: "#c3183c" }}>Technical Product Support:</span>{" "}
                      866.227.8525 or{" "}
                      <a 
                        href="mailto:techsupport@barksdale.com" 
                        style={{ 
                          color: "#1976d2", 
                          textDecoration: "underline",
                          textDecorationColor: "#1976d2"
                        }}
                      >
                        techsupport@barksdale.com
                      </a>
                    </Typography>
                  </Box>
                {/* )} */}
              </Box>
              <Divider
                sx={{
                  mx: "auto",
                  mt: 1,
                  borderColor: "#d01e42",
                  opacity: 0.4,
                  borderWidth: 1,
                }}
              />
              <Typography
                variant="body2"
                fontWeight="bold"
                mt={2}
                mb={4}
                pl={4}
                pr={2}
              >
                {initialBreadcrumbs}/
                {levelBreadcrumbs ? `${levelBreadcrumbs}/` : ""}
                <span style={{ color: "rgb(208, 30, 66)" }}>{breadcrumbs}</span>
              </Typography>
              <Box
                sx={{
                  minHeight: "calc(100vh - 400px)",
                  maxWidth: scrollableWidth,
                }}
              >
                {isLoading ? (
                  <>
                    <Box width={"38%"}>
                      <Skeleton />
                    </Box>
                    <Box width={"100%"}>
                      <Skeleton />
                    </Box>
                    <Box width={"100%"}>
                      <Skeleton />
                    </Box>
                    <Box width={"61%"}>
                      <Skeleton />
                    </Box>
                  </>
                ) : (
                  <Grid
                    container={container}
                    spacing={6}
                    justifyContent="center"
                    sx={{ margin: "0 auto", width: containerWidth }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        gap: 4,
                        border: "1px solid #e1e1e1",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                        borderRadius: 0,
                        overflow: isOverflow ? "hidden" : "visible",
                      }}
                    >
                      {containerTitle && (
                        <>
                          <Typography sx={{ p: "8px 32px", fontSize: "18px" }}>
                            {containerTitle}
                          </Typography>
                          <Divider
                            sx={{ borderColor: "#e1e1e1", borderWidth: 1 }}
                          />
                        </>
                      )}
                      <Box sx={{ p: 4 }}>{children}</Box>
                    </Paper>
                    {showSupport && (
                      <Box sx={{ p: 4, textAlign: "center" }}>
                        <Typography
                          variant="h4"
                          mt={6}
                          mb={4}
                          textAlign="center"
                        >
                          New Configurator! Click to Try it!
                        </Typography>

                        <Grid container spacing={7} justifyContent="center">
                          <Grid size={3} sx={{ width: "300px" }}>
                            <Paper
                              elevation={0}
                              sx={{
                                border: "1px solid #ccc",
                                p: 2,
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                window.open(
                                  "https://www.barksdale.com/tools-resources/configure-product",
                                  "_blank"
                                )
                              }
                            >
                              <Box
                                component="img"
                                src={IMAGES.productConfigurator}
                                alt="Product Configurator"
                                sx={{ width: 100, height: "auto" }}
                              />
                              <Box>
                                <Typography variant="h6" fontWeight="regular">
                                  Product
                                </Typography>
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  sx={{ color: "#d01e42" }}
                                >
                                  Configurator
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>

                          <Grid size={3} sx={{ width: "300px" }}>
                            <Paper
                              elevation={0}
                              sx={{
                                border: "1px solid #ccc",
                                pl: 0,
                                pr: 0,
                                pt: 2,
                                pb: 2,
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                window.open(
                                  "https://www.barksdale.com/contact-us",
                                  "_blank"
                                )
                              }
                            >
                              <Box
                                component="img"
                                src={IMAGES.technicalIssue}
                                alt="Technical Support"
                                sx={{ width: 100, height: "auto" }}
                              />
                              <Box>
                                <Typography
                                  variant="h6"
                                  fontWeight="bold"
                                  sx={{ textAlign: "center" }}
                                >
                                  Technical Issue?
                                </Typography>
                                <Typography
                                  variant="h6"
                                  fontWeight="regular"
                                  mt={1}
                                  sx={{ textAlign: "center", lineHeight: 1.3 }}
                                >
                                  For{" "}
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "#d01e42",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    FASTEST
                                  </Box>{" "}
                                  <br />
                                  Support
                                </Typography>
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Grid>
                )}
              </Box>
            </Box>
            <Footer />
          </Box>
        </Box>
        {/* Page code end here */}
      </Box>
    </Box>
  );
};

export default DashboardLayout;
